import { TargetPathPositionData } from 'ais-facade-api/api';
import { getDistance } from './getDistance';
import { getDiffInHours } from './getDiffInMinutes';

export interface ColorizedRouteFragment {
  inboundColor: number[];
  outboundColor: number[];
  from: TargetPathPositionData;
  to: TargetPathPositionData;
  speed: number | null;
}
export interface RouteFragment {
  from: TargetPathPositionData;
  to: TargetPathPositionData;
  speed: number | null;
}

/**
 * Функция возвращает цвет в формате RGB в зависимости от переданной скорости.
 *
 * @param {number | null} speed - Скорость в узлах.
 * @returns {number[]} - Цвет в формате RGB.
 *                        Первое число - красный канал, второе - желтый, третье - зеленый.
 */
const getColor = (speed: number | null) => {
  if (typeof speed !== 'number') return [84, 132, 255];
  if (speed < 4) return [255, 0, 0];
  if (speed >= 4 && speed <= 8) return [255, 255, 0];
  if (speed >= 8) return [0, 255, 0];
  return [84, 132, 255];
};

/**
 * Функция добавляет цветовую раскраску к маршруту на основе скорости движения.
 *
 * @param {TargetPathPositionData[] | undefined} route - Массив позиций для построения маршрута или undefined.
 * @returns {ColorizedRouteFragment[] | undefined} - Массив сегментов маршрута с цветовой раскраской или undefined.
 */
export const getColorizedRoute = (route: TargetPathPositionData[] | undefined): ColorizedRouteFragment[] | undefined => {
  if (!route) return;

  const preparedData = prepareData(route);
  if (!preparedData) return;

  const colorizedRoute = [];
  for (let i = 0; i < preparedData.length; i++) {
    let curSpeed, nextSpeed;
    if (i === preparedData.length - 1) {
      curSpeed = preparedData[i - 1].speed;
      nextSpeed = preparedData[i].speed;
    } else {
      curSpeed = preparedData[i].speed;
      nextSpeed = preparedData[i + 1].speed;
    }

    const colorizedFragment: ColorizedRouteFragment = {
      ...preparedData[i],
      inboundColor: getColor(curSpeed),
      outboundColor: getColor(nextSpeed),
    };

    colorizedRoute.push(colorizedFragment);
  }
  return colorizedRoute;
};

const prepareData = (data: TargetPathPositionData[] | undefined) => {
  if (!data) return;
  const array = [];
  const reversedPath: TargetPathPositionData[] = ([] as TargetPathPositionData[]).concat(data).reverse();
  for (let i = 0; i < reversedPath.length; i++) {
    let fromPoint, toPoint;
    if (i === reversedPath.length - 1) {
      fromPoint = reversedPath[i - 1];
      toPoint = reversedPath[i];
    } else {
      fromPoint = reversedPath[i];
      toPoint = reversedPath[i + 1];
    }

    if (
      typeof fromPoint?.lat !== 'number' ||
      typeof fromPoint?.lon !== 'number' ||
      typeof toPoint?.lat !== 'number' ||
      typeof toPoint?.lon !== 'number'
    ) {
      return;
    }

    const distance = getDistance({
      sourcePosition: [fromPoint.lon, fromPoint.lat],
      targetPosition: [toPoint.lon, toPoint.lat],
    });
    const time = getDiffInHours(toPoint.t, fromPoint.t);
    const obj = {
      from: fromPoint,
      to: toPoint,
      speed: time ? distance / time : null,
    };
    array.push(obj);
  }
  return array;
};
